/**
 * 세션 무효화 이벤트 버스.
 *
 * shared/auth 는 router/UI 에 의존하면 안 되므로(FSD 의존성 방향),
 * refresh 불가/실패로 세션이 무효화되면 여기서 이벤트만 emit 하고
 * features 계층(useAuthSessionRecovery)이 구독해 팝업·네비게이션을 처리한다.
 */
export type AuthSessionInvalidCode =
  | "REFRESH_TOKEN_EXPIRED"
  | "REFRESH_TOKEN_INVALID"
  | "REFRESH_TOKEN_REUSED"
  | "USER_WITHDRAWN"
  | "USER_SUSPENDED"
  /** 운영자 차단(무기한) — 정지(USER_SUSPENDED, 기간제)와 분리. 서버 activeViolation 우선순위: 탈퇴 > 차단 > 정지 */
  | "USER_BLOCKED"
  /** 클라이언트 판정 — 401 인데 refresh 수단(refresh token)이 없어 재로그인만 가능 */
  | "AUTH_TOKEN_MISSING"
  /** 서버 판정 — refresh 후 재시도까지 401 (서버 ErrorResponse.errorCode 와 동일 문자열) */
  | "UNAUTHORIZED"
  /** 클라이언트 판정 — 서버 401 확정 후, refresh 수단(refresh token) 없이 만료된 access token 만 있던 세션(리뷰 로그인 등). "로그인 만료" 안내 */
  | "SESSION_EXPIRED";

export type AuthSessionInvalidEvent = {
  errorCode: AuthSessionInvalidCode;
  /** 탈퇴/정지 안내 상세 메타 (§4-1 detail). 없으면 undefined. */
  detail?: Record<string, unknown> | null;
};

const listeners = new Set<(event: AuthSessionInvalidEvent) => void>();

export const subscribeAuthSessionInvalid = (
  listener: (event: AuthSessionInvalidEvent) => void,
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** 세션 무효는 새 토큰 확보까지 1회만 처리한다(#405, ADR 260918-01 — logOut→clear→옵저버 재요청→401 재진입 루프 차단). 해제는 tokenStorage.setToken 에서만 — 로그인 라우트 마운트에서 풀면 exit 중인 셸이 루프를 다시 연다. 이벤트 없이 끝난 세션(명시 로그아웃)은 markAuthSessionInvalidHandled 로 가드를 세운다. */
let sessionInvalidHandled = false;

export const markAuthSessionInvalidHandled = (): void => {
  sessionInvalidHandled = true;
};

export const resetAuthSessionInvalidGuard = (): void => {
  sessionInvalidHandled = false;
};

export const emitAuthSessionInvalid = (event: AuthSessionInvalidEvent): void => {
  if (sessionInvalidHandled) return;
  sessionInvalidHandled = true;
  listeners.forEach((listener) => listener(event));
};
